import { Component, OnInit, OnDestroy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';

import { Subscription } from "rxjs";
import { tap } from "rxjs/operators";


import { GridOptions, ColDef, GridApi } from "ag-grid-community";

import { ActionCellRendererComponent } from '../../../common/components/ag-grid/action-cell-renderer.component';
import { dateComparator } from '../../../common/components/data-table/comparators/data-table.comparators';

import { DateService } from "../../../common/services/date.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { PaymentService } from "../../services/payment.service";
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { PaymentSearchCriteria, PaymentStatus, ApplicationPayment, PaymentType, Payment } from "../../models/paymentItem.model";
import { SubHeader } from '../../../core/models/sub-header.model';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';

@Component({
    selector: 'app-payment-report',
    templateUrl: './payment-report.component.html',
    styleUrls: ['./payment-report.component.scss'],
    standalone: false
})

export class PaymentReportComponent implements OnInit, OnDestroy {

    payments: ApplicationPayment[];
	gridOptions: GridOptions;
	gridApi?: GridApi;
    currentDate: Date = this.dateService.generateDateWithOffset(0, 0, 1);
    previousDate : Date = this.dateService.generateDateWithOffset(0, -1, -1);
    toDate?: Date | null;
    fromDate?: Date | null;
    futureDateLimit?: Date | null;
    displayDateWarning: boolean = false;

    pastDateLimit?: Date | null;

    subHeader: SubHeader = new SubHeader({
        title: "Payment Report",
        buttons: [
            { name: "Download Report", method: () => this.downloadReport() },
            { name: "Filter By Date", method: () => this.generateReport() }
                 ]
    });

    disabledSubheader: SubHeader = new SubHeader({ title: "Payment Report" });
    disabledText: string = "";

    subscription: Subscription = new Subscription();

    invalidDatesDialog: Dialog = new Dialog("Invalid dates submitted, please assure from and to dates are not empty and that from date is not greater than to date", [new DialogButton("OK", true, () => { })]);


    constructor(
        private datePipe: DatePipe,
        private readonly dateService: DateService,
        private readonly formatService: GridFormatterService,
        private readonly paymentService: PaymentService,
        public router: Router,
        public subHeaderService: SubHeaderService,
        public dialogService: DynamicDialogService
	) { }

    ngOnInit() {
        this.subHeaderService.updateSubHeader(this.subHeader);
        this.fromDate = this.dateService.generateDateWithOffset(0, -1, 0);
        this.toDate = this.currentDate;
        this.futureDateLimit = new Date(this.currentDate);
        this.futureDateLimit.setDate(this.currentDate.getDate() + 1);
        this.pastDateLimit = new Date(this.fromDate);
        this.pastDateLimit.setDate(this.fromDate.getDate() - 1);

        

        let coldefs = this.createColumnDefinitions();
        this.gridOptions = this.formatService.BuildDefaultGridOptionsWithColumns(coldefs, false, true, true, true);
        this.gridOptions.overlayLoadingTemplate = '<span class="ag-overlay-loading-center">Please wait ...</span>';
		this.gridOptions.headerHeight = 50;
		this.gridOptions.onGridReady = (params) => { this.gridApi = params.api; };
        this.generateReport();
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }
 
    fromDateChange(model: Date | null): void {
        this.handleDateWarning(model, "fromDate");
        this.fromDate = model;
        if (this.fromDate != null) {
            this.pastDateLimit = new Date(this.fromDate);
            this.pastDateLimit.setDate(this.fromDate.getDate() - 1);
        }
    }

    toDateChange(model: Date | null): void {
        this.handleDateWarning(model, "toDate");
        this.toDate = model;
        if (this.toDate != null) {
            this.futureDateLimit = new Date(this.toDate);
            this.futureDateLimit.setDate(this.toDate.getDate() + 1);
        }
    }

    handleDateWarning(model: Date | null , origin: string): void {
        switch (origin) {
            case "toDate": {
                if (this.fromDate != null) {
                    if (model == null || model < this.fromDate) {
                        this.disabledText = 'To Date must not be empty and must be greater than From Date.';
                        this.displayDateWarning = true;
                        this.subHeaderService.updateSubHeader(this.disabledSubheader);
               
                    }
                    else if (model != null && this.fromDate == null) {
                        this.disabledText = 'From Date must not be empty and must be less than To Date.';
                        this.displayDateWarning = true;
                        this.subHeaderService.updateSubHeader(this.disabledSubheader);

                    }
                    else if (model > this.fromDate || model == this.fromDate) {
                        this.subHeaderService.updateSubHeader(this.subHeader);
                        this.displayDateWarning = false;
                    }
                }               
                break;
            }
            case "fromDate": {
                if (this.toDate != null) {
                    if (model == null || model > this.toDate) {
                        this.disabledText = 'From Date must not be empty and must be less than To Date.';
                        this.displayDateWarning = true;
                        this.subHeaderService.updateSubHeader(this.disabledSubheader);
                        this.fromDate = null; // Reset fromDate if invalid
                    }
                    else if (model != null && this.toDate == null) {
                        this.disabledText = 'To Date must not be empty and must be greater than From Date.';
                        this.displayDateWarning = true;
                        this.subHeaderService.updateSubHeader(this.disabledSubheader);
                    }
                    else if (model < this.toDate || model == this.toDate) {
                        this.subHeaderService.updateSubHeader(this.subHeader);
                        this.displayDateWarning = false;
                    }
                    break;
                }
            }
        }

    }

    generateReport() {
        if (this.gridApi) {
            this.gridApi.setGridOption("loading", true);
        }

        const criteria = new PaymentSearchCriteria("", "", [PaymentStatus.Paid]);
        criteria.fromDate = this.fromDate;
        criteria.toDate = this.toDate;

        if (criteria.fromDate == null || criteria.toDate == null || criteria.fromDate > criteria.toDate) {
            this.invalidDatesDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.invalidDatesDialog);
            if (this.gridApi != null) {
                this.gridApi.setGridOption("loading", false);
            }
            return;
                
        }

        this.subscription = this.paymentService.search(criteria).pipe(
            tap((data) => {
                this.payments = data; 
            })).subscribe({
                next: (success) => {
                    console.log("report retrieved");
                    const params = {
                        force: true
                    };
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("rowData", this.payments);
                        this.gridApi.setGridOption("loading", false);
                        this.gridApi.sizeColumnsToFit();
                    }
                }, 
                error: (fail) => {
                    console.error(fail);
                    if (this.gridApi != null) {
                        this.gridApi.setGridOption("loading", false);
                }
            }
            });
    }

    downloadReport() {

        if (this.fromDate == null || this.toDate == null || this.fromDate > this.toDate) {
            this.invalidDatesDialog.toggleDialog(true);
            this.dialogService.updateDialog(this.invalidDatesDialog);
            return;

        }
        const frmDt = this.datePipe.transform(this.fromDate, "yyyy-MM-dd").toString();
        const toDt = this.datePipe.transform(this.toDate, "yyyy-MM-dd").toString();
        const fileName = `RptPayment_${frmDt.toString()}_${toDt.toString()}`;
        this.formatService.exportDataToExcel(fileName, 'Payments', this.gridApi, false);
    }

    navigateToOrgDetail() {
        return (obj) => {
            const id = obj.data.organizationId;
            this.router.navigate(['/common/organization', id]);
        }
    }

    public createColumnDefinitions() {
        return [
            {
                headerClass: "wrapped",
                headerName: "Fee Type",
                headerTooltip: "Fee Type",
                field: "feeType",
                tooltipField: 'feeType',
                maxWidth: 200
            },
            {
                headerClass: "wrapped",
                headerName: "Application ID",
                headerTooltip: "Application ID",
                field: "applicationNumber",
                tooltipField: 'applicationNumber',
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Application Date",
                headerTooltip: "Application Date",
                field: "applicationDate",
                tooltipValueGetter: this.formatService.formatDate("applicationDate"),
                valueGetter: this.formatService.formatDate("applicationDate"),
                comparator: dateComparator,
                maxWidth: 135
            },
            {
                headerClass: "wrapped",
                headerName: "Payment Date",
                headerTooltip: "Payment Date",
                field: "paymentDate",
                tooltipValueGetter: this.formatService.formatDate("paymentDate"),
                valueGetter: this.formatService.formatDate("paymentDate"),
                comparator: dateComparator,
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Payment Posted Date",
                headerTooltip: "Payment Posted Date",
                field: "paymentPostedDate",
                tooltipValueGetter: this.formatService.formatDate("paymentPostedDate"),
                valueGetter: this.formatService.formatDate("paymentPostedDate"),
                comparator: dateComparator,
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Company Name",
                headerTooltip: "Company Name",
                field: "organization",
                tooltipField: 'organization',
                cellRenderer: ActionCellRendererComponent,
                cellRendererParams: {
                    textField: 'organization',
                    action: this.navigateToOrgDetail()
                },
                maxWidth: 225
            },
            {
                headerClass: "wrapped",
                headerName: "Company ID",
                headerTooltip: "Company ID",
                field: "organizationId",
                tooltipField: 'organizationId',
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "Amount",
                headerTooltip: "Amount",
                field: "amountPaid",
                tooltipValueGetter: this.formatService.formatCurrency("amountPaid", "USD", "symbol", "1.2-2"),
                valueFormatter: this.formatService.formatCurrency("amountPaid", "USD", "symbol", "1.2-2"),                
                filter: "agNumberColumnFilter",
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Type",
                headerTooltip: "Type",
                field: "paymentType",
                tooltipField: 'paymentType',
                valueGetter: this.typeFormatter(),
                tooltipValueGetter: this.typeFormatter(),
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Check / Money Order #",
                headerTooltip: "Check / Money Order #",
                valueGetter: this.formatCheckNumber(),
                tooltipValueGetter: this.formatCheckNumber(),
                maxWidth: 150
            },
            {
                headerClass: "wrapped",
                headerName: "CBOSS Authorization Code",
                headerTooltip: "CBOSS Authorization Code",
                valueGetter: this.formatAuthorization(),
                tooltipValueGetter: this.formatAuthorization(),
                maxWidth: 150
            },
            {
               headerClass: "wrapped",
               headerName: "Fund",
               headerTooltip: "Fund",
               field: "fund",
               tooltipField: 'fund',
               maxWidth: 100
            },
            {
                headerClass: "wrapped",
                headerName: "Department",
                headerTooltip: "Department",
                field: "department",
                tooltipField: 'department',
                maxWidth: 135,
                minWidth: 130
            },
            {
                headerClass: "wrapped",
                headerName: "ALI",
                headerTooltip: "ALI",
                field: "ali",
                tooltipField: 'ali',
                maxWidth: 100
            },
            {
                headerClass: "wrapped",
                headerName: "Program",
                headerTooltip: "Program",
                field: "program",
                tooltipField: 'program',
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Reporting ID",
                headerTooltip: "Reporting ID",
                field: "reporting",
                tooltipField: 'reporting',
                maxWidth: 125
            },
            {
                headerClass: "wrapped",
                headerName: "Revenue Source Code",
                headerTooltip: "Revenue Source Code",
                field: "revenueSourceCode",
                tooltipField: "revenueSourceCode",
                maxWidth: 150
            }

        ] as ColDef[];
    }


    formatAuthorization(): any {
        return (obj) => {
            let authCode = "";
            if (obj.data != null && obj.data["paymentRecords"] != null) {
                let records = <Payment[]>obj.data["paymentRecords"];
                if (records != null) {
                    authCode = this.formatToken(records, [PaymentType.ACH, PaymentType.Credit]);
                }
            }
            return authCode;
        };
    }

    formatCheckNumber(): any {
        return (obj) => {
            let checkNumber = "";
            if (obj.data != null && obj.data["paymentRecords"] != null) {
                let records = <Payment[]>obj.data["paymentRecords"];
                if (records != null) {
                    checkNumber = this.formatToken(records, [PaymentType.LocalCheck, PaymentType.MoneyOrder]);
                }
            }
            if (checkNumber.length === 0) checkNumber = "N/A";
            return checkNumber;
        };
    }

    formatToken(records: Payment[], types: PaymentType[]): string {
        let token = "";
        if (records != null) {
            let applicables = records.filter(r => types.includes(<PaymentType>r.paymentType));
            if (applicables != null && applicables.length > 0) {
                applicables.forEach(p => {
                    if (token.length > 0) token = `${token},`;
                    token = `${token}${p.tokenId}`;
                });
            }
        }
        return token;
    }

    onGridPageChanged(event: any) {
        if (this.gridApi != null) {
            this.gridApi.autoSizeAllColumns();
        }
    }

    typeFormatter(): any {
        return (obj) => {
            let value = "";
            if (obj.data != null && obj.data["paymentRecords"] != null) {
                let records = <Payment[]>obj.data["paymentRecords"];
                if (records != null && records.length > 0) {
                    records.forEach(r => {
                        let tempValue = "";
                        switch (r.paymentType) {
                            case PaymentType.ACH:
                                tempValue = "ACH";
                                break;
                            case PaymentType.LocalCheck:
                                tempValue = "Check";
                                break;
                            case PaymentType.PaidCash:
                                tempValue = "Cash";
                                break;
                            case PaymentType.MoneyOrder:
                                tempValue = "Money Order";
                                break;
                            default:
                                tempValue = r.paymentType;
                                break;
                        }
                        if (value.length > 0) value = `${value},`;
                        value = `${value}${tempValue}`;
                    })
                }
            }
            return value;
        }
    }
}
